'use client';

import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import { Mousewheel } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/mousewheel';
import styles from './TokenSwiper.module.scss';
import { TokenSwiperLink } from './TokenSwiperLink';

export interface Token {
  name: string;
  symbol: string;
  decimals: number;
  mint: string;
  imageUri: string;
  coingeckoId?: string | null;
  verified: boolean;
  price?: {
    price?: number;
    change?: number;
    usdPrice?: number;
    usdChange?: number;
    currency?: string;
    volume24h?: number;
    volumeChange24h?: number;
    liquidity?: number;
    mc?: number;
  };
  volume24h?: number | { total?: number; change?: number };
  liquidity?: number;
  marketCap?: number;
}

interface TokenSwiperProps {
  tokens: Token[];
  sidebar?: boolean;
  changeParameter?: 'price' | 'volume';
  count?: number;
}

export const TokenSwiper: React.FC<TokenSwiperProps> = ({ tokens, sidebar, changeParameter = 'price', count = 10 }) => {
  // Defensive: ensure tokens is always an array
  const safeTokens = Array.isArray(tokens) ? tokens : [];
  // Only include tokens with a valid price object
  const filteredTokens = safeTokens.filter(token => token.price);
  
  // Early return for empty states - prevents unnecessary DOM
  if (filteredTokens.length === 0) {
    return null;
  }

  const breakpoints = sidebar
    ? {
        480: { slidesPerView: 2.5 },
        768: { slidesPerView: 2.5 },
        980: { slidesPerView: 2.5 },
        1120: { slidesPerView: 2.5 },
        1200: { slidesPerView: 2.5 },
        1440: { slidesPerView: 2.5 },
      }
    : {
        480: { slidesPerView: 2.5 }, // small mobile
        768: { slidesPerView: 3.5 }, // mobile
        980: { slidesPerView: 4.5 }, // tablet
        1120: { slidesPerView: 5.5 }, // small desktop
        1200: { slidesPerView: 6.5 }, // large desktop
        1440: { slidesPerView: 7.5 }, // desktop
      };

  return (
    <Swiper
      modules={[Autoplay, Mousewheel]}
      slidesPerView={sidebar ? 2.5 : 1.5}
      breakpoints={breakpoints}
      spaceBetween={16} 
      navigation={false}
      pagination={false}
      loop={true}
      autoplay={{ delay: 4000, disableOnInteraction: false }}
      speed={500}
      mousewheel={{ enabled: true, forceToAxis: true }}
      freeMode={{
        enabled: true,
        momentum: true,
        momentumRatio: 0.5,
        momentumVelocityRatio: 0.5,
        momentumBounce: true,
        momentumBounceRatio: 0.5,
        minimumVelocity: 0.02,
        sticky: false,
      }}
      grabCursor={true}
      className={styles.swiperWrapper}
    >
      {filteredTokens.slice(0, count).map((token, idx) => (
        <SwiperSlide key={token.mint} className={styles.swiperSlide}>
          <TokenSwiperLink token={token} changeParameter={changeParameter} linkIndex={idx} />
        </SwiperSlide>
      ))}
    </Swiper>
  );
};
